/* eslint-disable import/max-dependencies */
import { useEffect } from "react";
import isEqual from "lodash.isequal";
import { useDispatch, useSelector } from "react-redux";
import {
  useHandleRequest,
  useIsAffiliate,
} from "@fas/cpa-state-manager/redux/hooks";
import {
  setFormData,
  getFormData,
  addNotification,
  setErrors,
  prepareFetchErrors,
  type ErrorsMapper,
} from "@fas/ui-framework";
import { PAYMENT_METHOD_FORM } from "../../constants";
import type { PaymentInfoType } from "../../services/deprecatedApi";
import { getPaymentInfo, postPaymentInfo } from "../../services/deprecatedApi";

type PaymentOtherField = {
  paymentMethodId: number;
  isEdit: boolean;
  currency: string;
};
type PaymentFieldFormType = Omit<PaymentInfoType, keyof PaymentOtherField>;
export type UsePaymentInfoFormHook = {
  isLoading: boolean;
  isChanged: boolean;
  disabled: boolean;
  paymentMethodId?: number;
  onSave: () => unknown;
  reset: () => unknown;
};
export const usePaymentInfoForm = (): UsePaymentInfoFormHook => {
  const dispatch = useDispatch();
  const { isLoading, changeLoading, onFail, onFinally } =
    useHandleRequest(PAYMENT_METHOD_FORM);
  const { isEdit, currency, paymentMethodId, ...form } = useSelector((state) =>
    getFormData(state, PAYMENT_METHOD_FORM)
  ) as PaymentInfoType;
  const clearForm: PaymentFieldFormType = Object.keys(form).reduce(
    (acc: PaymentFieldFormType, key: string): PaymentFieldFormType => ({
      ...acc,
      [key]: "",
    }),
    { ...form }
  );
  const clearErrorForm: ErrorsMapper = Object.keys(form).reduce(
    (acc: ErrorsMapper, key: string): ErrorsMapper => ({
      ...acc,
      [key]: {
        message: "",
      },
    }),
    {}
  );

  function loadForm() {
    changeLoading(true);
    getPaymentInfo()
      .then((data: PaymentInfoType) => {
        dispatch(setErrors(clearErrorForm));
        dispatch(setFormData(PAYMENT_METHOD_FORM, data));
      })
      .catch(onFail)
      .finally(onFinally);
  }

  useEffect(() => {
    loadForm();
  }, []);

  const onSave: () => unknown = () => {
    changeLoading(true);

    // clear field if not viewed in form // todo need add selector for connected fields to form
    function prepare(data: PaymentFieldFormType) {
      const fields = Object.keys(data) as Array<keyof typeof data>;
      const currentFields = [
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore todo
        ...document.querySelectorAll(
          fields.map((key) => `[data-testid=${key}]`).join(", ")
        ),
      ].map((node) => node.dataset.testid) as Array<keyof typeof data>;

      return fields.reduce(
        (acc, key) => ({
          ...acc,
          [key]: currentFields.includes(key) ? data[key] : null,
        }),
        data
      );
    }

    postPaymentInfo({
      ...prepare(form),
      paymentMethodId,
    })
      .then(() => {
        dispatch(
          addNotification({
            severity: "success",
            message: "Payment method updated successfully",
          })
        );
        loadForm();
      })
      .catch(async (err: any) => {
        await onFail(err);

        if (
          err.isAxiosError &&
          err.response &&
          err.response.data &&
          err.response.data.errors &&
          Array.isArray(err.response.data.errors)
        ) {
          dispatch(setErrors(prepareFetchErrors(err.response.data.errors)));
        }
      })
      .finally(() => {
        onFinally();
      });
  };

  const reset = () => {
    dispatch(setFormData(PAYMENT_METHOD_FORM, clearForm));
    dispatch(setErrors(clearErrorForm));
  };

  return {
    paymentMethodId,
    isLoading,
    disabled: !useIsAffiliate(),
    onSave,
    reset,
    isChanged: !isEqual(clearForm, form),
  };
};
